import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isApiClientError } from '../api/sdk';
import { getSiteSubscription } from '../api/billingApi';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import type { SubscriptionTier } from '../types/billing';
import { useDeleteSite } from './useDeleteSite';
import { usePublishSite } from './usePublishSite';
import { useRepublishSite } from './useRepublishSite';
import { useUpgradeSubscription } from './useUpgradeSubscription';
import { useCancelSubscription } from './useCancelSubscription';
import { useReactivateSubscription } from './useReactivateSubscription';

interface SiteSubscription {
  tier: SubscriptionTier | 'free';
  paid?: boolean;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | null;
}

interface UseSiteActionsOptions {
  onSubscriptionChange?: (subscription: SiteSubscription) => void;
  onDeleted?: () => void;
  /**
   * Called after a free-path publish/republish succeeds (no Stripe redirect).
   * The caller should refetch the site so the new `live` status is reflected.
   */
  onPublished?: () => void;
  /**
   * Optional subscription state for the delete flow. When provided, the hook
   * pre-cancels an active Stripe subscription before issuing DELETE so the
   * backend's 409-when-active-sub gate doesn't fire. When omitted, the hook
   * issues a bare DELETE — the backend will 409 if a sub is in fact active,
   * and the real error will be surfaced via toast.
   */
  subscription?: { hasActiveSub: boolean; cancelAtPeriodEnd: boolean };
}

/**
 * Hook for managing site actions (delete, republish, subscription management)
 * Shared between SiteCard and SiteDetail
 * Handles ApiClientError for better error messages
 */
export function useSiteActions(siteId: string, options: UseSiteActionsOptions = {}) {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { onSubscriptionChange, onDeleted, onPublished, subscription } = options;

  const deleteSite = useDeleteSite();
  const publishSiteMutation = usePublishSite();
  const republishSiteMutation = useRepublishSite();
  const upgradeSubscription = useUpgradeSubscription();
  const cancelSubscription = useCancelSubscription();
  const reactivateSubscription = useReactivateSubscription();

  // Tracks the entire delete flow (incl. the optional pre-cancel step) so the
  // dropdown's delete affordance stays disabled until the flow finishes.
  // Mutation.isPending alone misses the pre-cancel window.
  const [isDeleteFlow, setIsDeleteFlow] = useState(false);

  // Delete site
  const handleDelete = useCallback(async () => {
    if (!siteId) return;

    const confirmed = await confirm({
      title: 'Delete site',
      description: 'Are you sure you want to delete this site? This will destroy all infrastructure and cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive',
    });

    if (!confirmed) return;

    setIsDeleteFlow(true);
    try {
      if (subscription?.hasActiveSub && !subscription.cancelAtPeriodEnd) {
        await cancelSubscription.mutateAsync(siteId);
      }
      await deleteSite.mutateAsync(siteId);
      if (onDeleted) {
        onDeleted();
      } else {
        navigate('/sites');
      }
    } catch (err) {
      const message = isApiClientError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Failed to delete site';
      toast.error(message, 'Delete failed');
    } finally {
      setIsDeleteFlow(false);
    }
  }, [siteId, navigate, onDeleted, confirm, toast, subscription, cancelSubscription, deleteSite]);

  // Re-publish a `suspended` or `offline` site. Backend decides free vs paid:
  // free path flips status back to `live`; paid path returns a checkout URL
  // that we redirect to. On free-path success we invoke `onPublished` so the
  // caller can refetch the site row.
  const handleRepublish = useCallback(async () => {
    if (!siteId) return;

    try {
      const result = await republishSiteMutation.mutateAsync(siteId);
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      toast.success('Check back in a few minutes.', 'Re-publish started');
      if (onPublished) onPublished();
    } catch (error) {
      console.error('Republish failed:', error);
      const message = isApiClientError(error) ? error.message : (error instanceof Error ? error.message : 'Failed to republish site. Please try again.');
      toast.error(message, 'Re-publish failed');
    }
  }, [siteId, toast, onPublished, republishSiteMutation]);

  // Publish a `draft` site. Free-pass eligible → backend flips status to
  // `live` and returns `checkoutUrl: null`. Otherwise → caller redirects to
  // the returned Stripe Checkout URL.
  const handlePublish = useCallback(async () => {
    if (!siteId) return;

    try {
      const result = await publishSiteMutation.mutateAsync(siteId);
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      toast.success('Your site is going live.', 'Site published');
      if (onPublished) onPublished();
    } catch (error) {
      console.error('Publish failed:', error);
      const message = isApiClientError(error) ? error.message : (error instanceof Error ? error.message : 'Failed to publish site.');
      toast.error(message, 'Publish failed');
    }
  }, [siteId, toast, onPublished, publishSiteMutation]);

  // Upgrade a `live` free site (e.g. add a custom domain). Always paid path:
  // backend creates a Stripe Checkout session and we redirect.
  const handleUpgrade = useCallback(async () => {
    if (!siteId) return;

    try {
      const result = await upgradeSubscription.mutateAsync(siteId);
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        toast.error('Failed to create checkout session', 'Upgrade failed');
      }
    } catch (error) {
      console.error('Failed to upgrade site:', error);
      const message = isApiClientError(error) ? error.message : (error instanceof Error ? error.message : 'Failed to start upgrade process');
      toast.error(message, 'Upgrade failed');
    }
  }, [siteId, toast, upgradeSubscription]);

  // Cancel subscription
  const handleCancelSubscription = useCallback(async () => {
    if (!siteId) return;

    const confirmed = await confirm({
      title: 'Cancel subscription',
      description: 'Cancel this site subscription? Your site will revert to free tier at the end of the billing period.',
      confirmText: 'Cancel subscription',
      cancelText: 'Keep subscription',
      variant: 'destructive',
    });

    if (!confirmed) return;

    try {
      const result = await cancelSubscription.mutateAsync(siteId);
      toast.info(result.message || 'Subscription will be canceled at period end.', 'Cancellation scheduled');
      // Refresh subscription data so callers receive the updated state. The
      // mutation also invalidates the per-site subscription key, but the
      // imperative re-fetch keeps the existing onSubscriptionChange contract.
      // Best-effort: a failed refresh must not surface as a cancel failure.
      const refreshed = await getSiteSubscription(siteId).catch(() => null);
      if (refreshed && onSubscriptionChange) {
        onSubscriptionChange(refreshed);
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      const message = isApiClientError(error) ? error.message : (error instanceof Error ? error.message : 'Failed to cancel subscription');
      toast.error(message, 'Cancellation failed');
    }
  }, [siteId, onSubscriptionChange, confirm, toast, cancelSubscription]);

  // Reactivate subscription
  const handleReactivateSubscription = useCallback(async () => {
    if (!siteId) return;

    try {
      await reactivateSubscription.mutateAsync(siteId);
      toast.success('Your subscription is active again.', 'Subscription reactivated');
      // Refresh subscription data. Best-effort: a failed refresh must not
      // surface as a reactivation failure.
      const refreshed = await getSiteSubscription(siteId).catch(() => null);
      if (refreshed && onSubscriptionChange) {
        onSubscriptionChange(refreshed);
      }
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
      const message = isApiClientError(error) ? error.message : (error instanceof Error ? error.message : 'Failed to reactivate subscription');
      toast.error(message, 'Reactivation failed');
    }
  }, [siteId, onSubscriptionChange, toast, reactivateSubscription]);

  // Navigate to edit/detail page
  const handleEdit = useCallback(() => {
    navigate(`/sites/${siteId}`);
  }, [siteId, navigate]);

  // `isDeleteFlow` covers the entire delete flow including the pre-cancel
  // step; `cancelSubscription.isPending` is intentionally excluded so a
  // standalone cancel doesn't leak into the delete indicator.
  const isDeleting = isDeleteFlow || deleteSite.isPending;
  const isPublishing = publishSiteMutation.isPending;
  const isRepublishing = republishSiteMutation.isPending;
  const isCancellingSubscription = cancelSubscription.isPending;
  const isReactivatingSubscription = reactivateSubscription.isPending;

  return {
    // Actions
    handleDelete,
    handlePublish,
    handleRepublish,
    handleUpgrade,
    handleCancelSubscription,
    handleReactivateSubscription,
    handleEdit,

    // Loading states
    isDeleting,
    isPublishing,
    isRepublishing,
    isCancellingSubscription,
    isReactivatingSubscription,
    isLoading: isDeleting || isPublishing || isRepublishing || isCancellingSubscription || isReactivatingSubscription
  };
}

export type UseSiteActionsReturn = ReturnType<typeof useSiteActions>;
